import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Tooltip } from "@mui/material";
import { DockItemData } from "./Dock";

interface SortableDockItemProps {
  item: DockItemData;
  scale: number;
  defaultSize: number;
  isVertical?: boolean;
}

const SortableDockItem: React.FC<SortableDockItemProps> = ({
  item,
  scale,
  defaultSize,
  isVertical = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : "auto",
  };

  const handleClick = () => {
    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <Tooltip
      title={item.label}
      placement={isVertical ? "right" : "top"}
      arrow
    >
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={handleClick}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-end",
          cursor: isDragging ? "grabbing" : "grab",
          transition:
            "transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
          transform: `scale(${scale})`,
          transformOrigin: isVertical ? "center center" : "bottom center",
          width: defaultSize,
          height: defaultSize,
          touchAction: "none",
          "&:hover": {
            "& .dock-item-icon": {
              filter: "brightness(1.1)",
            },
          },
        }}
      >
        {/* Icon */}
        <Box
          className="dock-item-icon"
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "12px",
            overflow: "hidden",
            transition: "filter 0.2s ease",
            "& img": {
              width: "100%",
              height: "100%",
              objectFit: "cover",
            },
            "& svg": {
              width: "100%",
              height: "100%",
            },
          }}
        >
          {item.icon}
        </Box>

        {/* Running Indicator Dot */}
        {item.isRunning && (
          <Box
            sx={{
              position: "absolute",
              bottom: -6,
              left: "50%",
              transform: "translateX(-50%)",
              width: 4,
              height: 4,
              borderRadius: "50%",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "#fff" : "#000",
              boxShadow: "0 0 4px rgba(0, 0, 0, 0.3)",
            }}
          />
        )}
      </Box>
    </Tooltip>
  );
};

export default SortableDockItem;
